import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchDataCategories } from "../../../../features/auth/store/dataCategorySlice";
import DataCategoriesTable from "./DataCategoriesTable";
import { CreateDataCategoryModal } from "../../Modal/Data/CreateDataCategoryModal";
import {
  ExternalContainer,
  InternalContainer,
} from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { EditDataCategoryModal } from "../../Modal/Data/EditDataCategoryModal";
import { DeleteDataCategoryModal } from "../../Modal/Data/DeleteDataCategoryModal";

const DataCategories: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const { dataCategories, loading } = useSelector(
    (state: RootState) => state.dataCategories,
  );

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      dispatch(fetchDataCategories({}));
      return;
    }

    const timer = setTimeout(() => {
      dispatch(
        fetchDataCategories({
          search: searchTerm.trim() || undefined,
          status: statusFilter || undefined,
        }),
      );
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, dispatch]);

  const openDeleteModal = async (id: number) => {
    setSelectedCategory(id);
    setIsDeleteModalOpen(true);
  };

  const openEditModal = async (id: number) => {
    setSelectedCategory(id);
    setIsEditModalOpen(true);
  };

  return (
    <ExternalContainer>
      <div className="h-full min-h-screen dark:bg-[#FFFFFF1A] w-full max-w-6xl mx-auto px-4 py-6">
        <InternalContainer headerText="Data Categories">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <input
                type="text"
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border p-2 rounded w-full sm:w-64"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border p-2 rounded w-full sm:w-40"
              >
                <option value="">All Status</option>
                <option value="on">Active</option>
                <option value="off">Inactive</option>
              </select>
            </div>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="w-full sm:w-[200px] h-10"
            >
              New Category
            </Button>
          </div>

          <DataCategoriesTable
            categories={dataCategories}
            loading={loading}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            onDelete={openDeleteModal}
            onEdit={openEditModal}
          />

          <CreateDataCategoryModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
          />
          <EditDataCategoryModal
            isOpen={isEditModalOpen}
            id={selectedCategory}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedCategory(null);
            }}
          />
          <DeleteDataCategoryModal
            id={selectedCategory}
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedCategory(null);
            }}
          />
        </InternalContainer>
      </div>
    </ExternalContainer>
  );
};

export default DataCategories;
